import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Info } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import Icon from "../components/Icon";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { CONCERNS, CONCERN_NOTE } from "../data/lifeConcerns";
import { getService } from "../data/services";

export default function LifeConcerns() {
  const { t, pick, lang } = useLang();
  const { openBooking } = useBooking();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo
        title="Life Concerns & Guidance Hub"
        description="Respectful spiritual and traditional guidance pathways for real-life concerns — love, marriage, family, finance, career, children, protection, peace and spiritual growth."
        path="/life-concerns"
        keywords={["spiritual guidance", "life problems remedies", "puja for problems", "Tarapith guidance"]}
      />
      <Breadcrumbs items={[{ label: t("nav_concerns") }]} />

      <div className="max-w-2xl mb-8">
        <span className="text-xs font-serif tracking-[0.25em] uppercase text-tara-gold/90">{t("concerns_eyebrow")}</span>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-tara-ink mt-2 mb-3">{t("concerns_title")}</h1>
      </div>

      <div className="glass rounded-2xl p-5 mb-10 flex gap-3 items-start" data-testid="concern-note">
        <Info className="h-5 w-5 text-tara-gold shrink-0 mt-0.5" />
        <p className="text-sm text-tara-muted leading-relaxed">{CONCERN_NOTE[lang] || CONCERN_NOTE.en}</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {CONCERNS.map((c, i) => {
          const svc = getService(c.serviceId);
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
              className="glass glass-reflect hover-lift rounded-2xl p-6 flex flex-col"
              data-testid={`concern-card-${c.id}`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/70 border border-tara-gold/30 mb-4">
                <Icon name={c.icon} className="h-6 w-6 text-tara-gold" />
              </span>
              <h2 className="font-display text-xl font-semibold text-tara-ink mb-2">{pick(c, "title")}</h2>
              <p className="text-sm text-tara-muted leading-relaxed mb-5 flex-1">
                People may seek spiritual guidance or traditional religious practices regarding this concern. Guidance can help
                you understand available devotional options.
              </p>
              <button
                onClick={() => openBooking(c.serviceId, pick(c, "title"))}
                className="inline-flex items-center gap-2 text-sm font-serif font-semibold text-tara-gold hover:gap-3 transition-all"
                data-testid={`concern-cta-${c.id}`}
              >
                <MessageCircle className="h-4 w-4" /> {t("cta_discuss")}
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
